import React, { useState, useMemo, useEffect } from 'react';
import './App.css';
import EmployeeCard from './components/EmployeeCard';
import SummaryCard from './components/SummaryCard';
import FileUpload from './components/FileUpload';
import LoginForm from './components/LoginForm';
import EmployeeDashboard from './components/EmployeeDashboard';
import { mockEmployees, summaryData } from './data/mockData';
import { Input } from './components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Search, Filter, FileText, Download, Upload, RefreshCw, Database, LogOut } from 'lucide-react';
import { Button } from './components/ui/button';
import { useToast } from './hooks/use-toast';
import { Toaster } from './components/ui/toaster';
import { extractDateFromFilename } from './lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const EMPLOYEES_API_URL = BACKEND_URL ? `${BACKEND_URL}/api/employees` : '/api/employees';
const SUMMARY_API_URL = BACKEND_URL ? `${BACKEND_URL}/api/employees/summary` : '/api/employees/summary';

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filterBy, setFilterBy] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('mock'); // 'mock' или 'api'
  const [authHeader, setAuthHeader] = useState(null); // Для хранения заголовка авторизации
  const [currentFilename, setCurrentFilename] = useState(null); // Для хранения названия загруженного файла
  const [userRole, setUserRole] = useState(null); // 'admin' or 'employee'
  const { toast } = useToast();

  // Загрузка данных из API
  const fetchEmployees = async () => {
    if (dataSource === 'mock') return;
    
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (sortBy) params.append('sortBy', sortBy);
      if (filterBy) params.append('filterBy', filterBy);
      
      // Добавляем заголовок авторизации к запросу
      const config = {};
      if (authHeader) {
        config.headers = { 'Authorization': authHeader };
      }
      
      const response = await axios.get(`${EMPLOYEES_API_URL}?${params}`, config);
      setEmployees(response.data.employees);
    } catch (err) {
      console.error('Error fetching employees:', err);
      
      // Если ошибка авторизации, сбрасываем заголовок авторизации
      if (err.response && err.response.status === 401) {
        setAuthHeader(null);
        setUserRole(null);
        setError('Сесія закінчилася. Будь ласка, увійдіть знову.');
      } else {
        setError('Помилка завантаження даних');
      }
      
      toast({
        title: "Помилка",
        description: "Не вдалося завантажити дані співробітників",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    if (dataSource === 'mock') return;
    
    try {
      // Добавляем заголовок авторизации к запросу
      const config = {};
      if (authHeader) {
        config.headers = { 'Authorization': authHeader };
      }
      
      const response = await axios.get(SUMMARY_API_URL, config);
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
      
      // Если ошибка авторизации, сбрасываем заголовок авторизации
      if (err.response && err.response.status === 401) {
        setAuthHeader(null);
        setUserRole(null);
      }
    }
  };

  // Проверяем наличие данных в API при загрузке
  useEffect(() => {
    const checkDataSource = async () => {
      try {
        // Добавляем заголовок авторизации к запросу
        const config = {};
        if (authHeader) {
          config.headers = { 'Authorization': authHeader };
        }
        
        const response = await axios.get(`${EMPLOYEES_API_URL}?limit=1`, config);
        if (response.data.employees.length > 0) {
          setDataSource('api');
        }
      } catch (err) {
        console.log('Using mock data');
        
        // Если ошибка авторизации, сбрасываем заголовок авторизации
        if (err.response && err.response.status === 401) {
          setAuthHeader(null);
          setUserRole(null);
        }
        setDataSource('mock');
      }
    };
    
    checkDataSource();
  }, [authHeader]);

  // Загружаем данные при изменении источника или параметров
  useEffect(() => {
    if (dataSource === 'api') {
      fetchEmployees();
      fetchSummary();
    }
  }, [dataSource, searchTerm, sortBy, filterBy]);

  // Мок данные с фильтрацией (используем когда нет данных в API)
  const filteredAndSortedEmployees = useMemo(() => {
    if (dataSource === 'api') return employees;
    
    let filtered = mockEmployees.filter(employee => 
      employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.grade.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filterBy !== 'all') {
      switch (filterBy) {
        case 'high-performance':
          filtered = filtered.filter(emp => emp.performance.qualityScore >= 90);
          break;
        case 'needs-attention':
          filtered = filtered.filter(emp => 
            emp.workMetrics.maintenance.notCompleted > 0 || 
            emp.workMetrics.maintenance.issuesNotFixed > 0 || 
            emp.workMetrics.maintenance.lateResponse > 0
          );
          break;
        case 'senior':
          filtered = filtered.filter(emp => emp.grade.includes('6') || emp.position.includes('Старший'));
          break;
        default:
          break;
      }
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'salary':
          return b.salary.netPay - a.salary.netPay;
        case 'performance':
          return b.performance.qualityScore - a.performance.qualityScore;
        case 'elevators':
          return b.workMetrics.totalElevators - a.workMetrics.totalElevators;
        case 'name':
        default:
          return a.name.localeCompare(b.name, 'uk');
      }
    });

    return filtered;
  }, [searchTerm, sortBy, filterBy, employees, dataSource]);

  const handleUploadSuccess = (uploadData) => {
    setDataSource('api');
    // Сохраняем название файла если оно есть
    if (uploadData.filename) {
      setCurrentFilename(uploadData.filename);
    }
    toast({
      title: "Успіх!",
      description: `Завантажено ${uploadData.employees_count} співробітників`,
      variant: "default",
    });
    
    // Обновляем данные
    fetchEmployees();
    fetchSummary();
  };

  const handleRefreshData = () => {
    if (dataSource === 'api') {
      fetchEmployees();
      fetchSummary();
    }
  };

  // Add a function to handle employee updates
  const handleEmployeeUpdate = (id, updates) => {
    // Update the employees state
    setEmployees(prevEmployees => 
      prevEmployees.map(emp => 
        emp.id === id ? { ...emp, ...updates } : emp
      )
    );
    
    // If we're using mock data, also update the mock data
    // In a real app, you might want to refresh from the API instead
  };

  const currentSummary = dataSource === 'api' ? summary : summaryData;
  const currentEmployees = filteredAndSortedEmployees;

  // Если пользователь не авторизован, показываем форму входа
  if (!authHeader) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LoginForm onLogin={(authData) => {
          setAuthHeader(authData.header);
          setUserRole(authData.role || 'admin');
        }} />
        <Toaster />
      </div>
    );
  }

  // Если пользователь - сотрудник, показываем его персональную панель
  if (userRole === 'employee') {
    return (
      <EmployeeDashboard 
        authHeader={authHeader} 
        onLogout={() => {
          setAuthHeader(null);
          setUserRole(null);
        }} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Звіт по зарплаті</h1>
              <div className="flex items-center space-x-2">
                <p className="text-sm text-gray-600">{extractDateFromFilename(currentFilename)} року</p>
                {dataSource === 'api' && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                    <Database className="w-3 h-3 mr-1" />
                    API данні
                  </span>
                )}
                {dataSource === 'mock' && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                    Тестові данні
                  </span>
                )}
              </div>
            </div>
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefreshData}
                disabled={loading}
                className="flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Оновити</span>
              </Button>
              <Button 
                variant="default" 
                size="sm" 
                onClick={() => setShowUpload(true)}
                className="flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Завантажити</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setAuthHeader(null);
                  setUserRole(null);
                }}
                className="flex items-center space-x-2"
              >
                <LogOut className="w-4 h-4" />
                <span>Вийти</span>
              </Button>
            </div>
          </div>
          
          {/* Search and Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input 
                placeholder="Пошук по імені, посаді або розряду..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex space-x-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Сортування" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">За іменем</SelectItem>
                  <SelectItem value="salary">За зарплатою</SelectItem>
                  <SelectItem value="performance">За ефективністю</SelectItem>
                  <SelectItem value="elevators">За кількістю ліфтів</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterBy} onValueChange={setFilterBy}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Фільтр" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Всі співробітники</SelectItem>
                  <SelectItem value="high-performance">Високі показники</SelectItem>
                  <SelectItem value="needs-attention">Потребує уваги</SelectItem>
                  <SelectItem value="senior">Старші співробітники</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Summary */}
        {currentSummary && <SummaryCard summaryData={currentSummary} filename={currentFilename} />}
        
        {/* Loading state */}
        {loading && (
          <Card className="mb-6">
            <CardContent className="py-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Завантаження даних...</p>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {error && !loading && (
          <Card className="mb-6 border-red-200">
            <CardContent className="py-12 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">Помилка завантаження</h3>
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={handleRefreshData} variant="outline">
                Спробувати знову
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Results count */}
        {!loading && !error && (
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Знайдено {currentEmployees.length} з {dataSource === 'api' ? (employees.length || 0) : mockEmployees.length} співробітників
            </p>
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Експорт</span>
            </Button>
          </div>
        )}
        
        {/* Employee Cards */}
        {!loading && !error && (
          <div className="space-y-4">
            {currentEmployees.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-6 h-6 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Нічого не знайдено</h3>
                  <p className="text-gray-600 mb-4">
                    Спробуйте змінити критерії пошуку або фільтрування
                  </p>
                  {dataSource === 'mock' && (
                    <Button onClick={() => setShowUpload(true)} className="flex items-center space-x-2 mx-auto">
                      <Upload className="w-4 h-4" />
                      <span>Завантажити Excel файл</span>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              currentEmployees.map(employee => (
                <EmployeeCard 
                  key={employee.id} 
                  employee={employee} 
                  authHeader={authHeader}
                  userRole={userRole}
                  onEmployeeUpdate={handleEmployeeUpdate}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>© 2024 Система управління персоналом. Звіт згенеровано автоматично.</p>
        </div>
      </footer>

      {/* File Upload Modal */}
      {showUpload && (
        <FileUpload
          onUploadSuccess={handleUploadSuccess}
          onClose={() => setShowUpload(false)}
          authHeader={authHeader}
        />
      )}

      <Toaster />
    </div>
  );
}

export default App;